import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../App.css';
import { config } from '../config';
import { useMsal } from '@azure/msal-react';
import QRScannerView from '../components/QRScannerView';
import QrScanner from 'qr-scanner';
import { validateSKReceiptQR } from '../utils/qrValidation';
import { trackEvent } from '../utils/posthog';
import { getAuthToken } from '../utils/authToken';
import BillSplitFlow from '../components/BillSplitFlow/BillSplitFlow';
import PhotoUploadFlow from '../components/BillCapture/PhotoUploadFlow';
import NonSkReceiptFlow from '../components/NonSkReceiptFlow/NonSkReceiptFlow';

function AddReceiptPage() {
  const { instance, accounts } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();

  // Helper to make authenticated API calls
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = await getAuthToken(instance);
    if (!token) {
      throw new Error('Authentication required');
    }

    const headers = {
      ...options.headers,
      'X-Auth-Token': `Bearer ${token}`
    };

    // Create AbortController for timeout (120 seconds for OpenAI Vision calls)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);

    try {
      const response = await fetch(url, { ...options, headers, signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Požiadavka trvala príliš dlho (viac ako 2 minúty)');
      }
      throw error;
    }
  };

  // Helper to show error messages
  const showError = (message: string) => {
    setErrorMessage(message);
    setStatusMessage('');
    setIsProcessing(false);
    // Auto-hide after 8 seconds
    setTimeout(() => setErrorMessage(''), 8000);
  };

  // Helper to show success messages
  const showSuccessMessage = (message: string) => {
    setStatusMessage('');
    setErrorMessage('');
    setStatusMessage('✅ ' + message);
    setTimeout(() => setStatusMessage(''), 4000);
  };

  // View mode: 'scanner' (default), 'photo-upload', 'manual-uid', 'non-sk'
  const [viewMode, setViewMode] = useState<'scanner' | 'photo-upload' | 'manual-uid' | 'non-sk'>('scanner');
  const [moreBills, setMoreBills] = useState('no');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [manualUid, setManualUid] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingSuccess, setProcessingSuccess] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showNoteConfirmation, setShowNoteConfirmation] = useState(false);
  const [receiptData, setReceiptData] = useState<any>(null);
  const [note, setNote] = useState('');
  const [savedNote, setSavedNote] = useState('');
  const [noteStatus, setNoteStatus] = useState<{ message: string; type: 'success' | 'error' | '' }>({ message: '', type: '' });
  const [isSavingNote, setIsSavingNote] = useState(false);

  // Non-SK receipt states
  const [showNonSkFlow, setShowNonSkFlow] = useState(false);

  // Bill split states
  const [showSplitFlow, setShowSplitFlow] = useState(false);
  const [splitToken, setSplitToken] = useState<string>('');
  const [isSplitProcessing, setIsSplitProcessing] = useState(false);
  const [splitFromReceiptDetail, setSplitFromReceiptDetail] = useState(false);
  const [splitReceiptId, setSplitReceiptId] = useState<string | null>(null);


  // Listen for menu navigation to reset to scanner view
  useEffect(() => {
    const handleResetToScanner = () => {
      setViewMode('scanner');
      setShowNonSkFlow(false);
      setShowSuccess(false);
      setShowNoteConfirmation(false);
      setShowSplitFlow(false);
    };

    window.addEventListener('resetToScanner', handleResetToScanner);
    return () => window.removeEventListener('resetToScanner', handleResetToScanner);
  }, []);


  // Handle split flow initiated from receipt detail page
  useEffect(() => {
    const state = location.state as any;
    if (state?.fromReceiptDetail && state?.splitToken && state?.receiptId) {
      // Simply set up the basic state and let BillSplitFlow handle the rest
      setSplitToken(state.splitToken);
      setSplitFromReceiptDetail(true);
      setSplitReceiptId(state.receiptId);
      setReceiptData({ receipt_id: state.receiptId });
      setShowSplitFlow(true);

      // Track split started (edit mode)
      trackEvent('split_started', {
        receipt_id: state.receiptId,
        is_edit: true
      });

      // Clear the navigation state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
  };

  const handleSaveNote = async () => {
    if (!note.trim()) {
      setNoteStatus({ message: 'Zadaj poznámku pred uložením', type: 'error' });
      return;
    }

    setIsSavingNote(true);
    setNoteStatus({ message: '', type: '' });

    try {
      // Call backend API to save the note
      const response = await authenticatedFetch(`${config.apiUrl}/saveReceiptNote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          receipt_id: receiptData.receipt_id,  // Use receipt_id (R_ prefixed) not receipt_hash
          note: note.trim()
        })
      });

      if (response.ok) {
        await response.json();

        // Store the saved note and show confirmation page
        setSavedNote(note.trim());
        setShowSuccess(false);
        setShowNoteConfirmation(true);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Chyba pri ukladaní');
      }
    } catch (error) {
      setNoteStatus({ message: '❌ Chyba pri ukladaní poznámky', type: 'error' });
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleNewReceipt = () => {
    setShowSuccess(false);
    setShowNoteConfirmation(false);
    setReceiptData(null);
    setNote('');
    setSavedNote('');
    setNoteStatus({ message: '', type: '' });
    setSelectedFile(null);
    setManualUid('');
    setStatusMessage('');
    // Reset non-SK flow (state is now managed internally by NonSkReceiptFlow component)
    setShowNonSkFlow(false);
    // Reset split flow
    setShowSplitFlow(false);
    setSplitToken('');
    setIsSplitProcessing(false);
    // Return to QR scanner view
    setViewMode('scanner');
  };

  // Handle receipt processed from PhotoUploadFlow
  const handleReceiptProcessed = (receiptDataFromUpload: any) => {
    setReceiptData(receiptDataFromUpload);
    setShowSuccess(true);
  };

  const formatDateTime = (datetime: string) => {
    try {
      return new Date(datetime).toLocaleString('sk-SK');
    } catch {
      return datetime;
    }
  };

  // QR Scanner callbacks
  const handleQRScanSuccess = async (uid: string, format: 'online' | 'offline') => {
    console.log('[AddReceiptPage] QR scanned successfully:', uid, format);
    try {
      await processReceiptUID(uid);
    } catch (error: any) {
      console.error('[AddReceiptPage] Error in handleQRScanSuccess:', error);
      showError(`Chyba pri spracovaní: ${error.message || 'Neznáma chyba'}`);
      setIsProcessing(false);
    }
  };

  const handlePhotoUploadClick = () => {
    setViewMode('photo-upload');
  };

  const handleManualInputClick = () => {
    setViewMode('manual-uid');
  };

  const handleNonSkClick = () => {
    setViewMode('non-sk');
    setShowNonSkFlow(true);
  };

  // Process receipt UID (from QR scan or manual input)
  const processReceiptUID = async (uid: string) => {
    console.log('[processReceiptUID] Starting with UID:', uid);
    setIsProcessing(true);
    setProcessingSuccess(false); // Reset success flag
    setStatusMessage('🔄 Validujem bloček s eKasa...');
    setErrorMessage(''); // Clear any previous errors

    try {
      console.log('[processReceiptUID] Getting auth token...');
      setStatusMessage('🔑 Získavam autentifikáciu...');
      const token = await getAuthToken(instance);
      console.log('[processReceiptUID] Auth token received:', token ? 'YES' : 'NO');
      if (!token) {
        throw new Error('Chyba autentifikácie - skúste sa odhlásiť a prihlásiť');
      }

      console.log('[processReceiptUID] Calling getReceiptFromeKasa...');
      setStatusMessage('📡 Overujem bloček na eKasa...');

      // Generate uploadId for QR scan (backend requires it)
      const uploadId = `QR_SCAN_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Show progress for long-running requests
      const progressTimer = setTimeout(() => {
        setStatusMessage('⏳ eKasa server je pomalý, čakám...');
      }, 10000); // After 10 seconds

      const slowTimer = setTimeout(() => {
        setStatusMessage('⏳ Stále pracujem, môže to trvať až minútu...');
      }, 30000); // After 30 seconds

      let response;
      try {
        response = await fetch(`${config.apiUrl}/getReceiptFromeKasa`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Auth-Token': `Bearer ${token}`
          },
          body: JSON.stringify({
            uploadId: uploadId,
            manualUid: uid
          })
        });
      } catch (networkError: any) {
        clearTimeout(progressTimer);
        clearTimeout(slowTimer);
        throw new Error('Problém s pripojením na server. Skontrolujte internet.');
      } finally {
        clearTimeout(progressTimer);
        clearTimeout(slowTimer);
      }

      console.log('[processReceiptUID] Response status:', response.status);

      if (response.status === 422) {
        const htmlContent = await response.text();
        document.open();
        document.write(htmlContent);
        document.close();
        return;
      }

      if (!response.ok) {
        let errorMsg = `Server error (${response.status})`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.message || errorMsg;
        } catch (e) {
          // Response wasn't JSON
        }
        throw new Error(errorMsg);
      }

      const validationResult = await response.json();
      console.log('[processReceiptUID] Validation successful:', validationResult);
      setStatusMessage('✅ Bloček overený!');
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief pause to show success

      // Store receipt
      setStatusMessage('💾 Ukladám bloček do databázy...');
      console.log('[processReceiptUID] Calling storeReceiptOnly...');
      console.log('[processReceiptUID] moreBills state:', moreBills);

      // Use the same uploadId from validation
      let storeResponse;
      try {
        storeResponse = await fetch(`${config.apiUrl}/storeReceiptOnly`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Auth-Token': `Bearer ${token}`
          },
          body: JSON.stringify({
            uploadId: uploadId,
            validationResult,
            action: 'store',
            moreBills: moreBills
          })
        });
      } catch (networkError: any) {
        throw new Error('Problém s pripojením pri ukladaní. Skontrolujte internet.');
      }

      console.log('[processReceiptUID] Store response status:', storeResponse.status);

      // Handle duplicate receipt error
      if (storeResponse.status === 409) {
        const errorData = await storeResponse.json();
        console.log('[processReceiptUID] Duplicate receipt detected:', errorData);

        setStatusMessage('');
        setIsProcessing(false);

        // Show duplicate receipt error
        setReceiptData({
          isDuplicate: true,
          receipt_hash: errorData.receipt_id,
          message: errorData.user_message || 'Tento bloček je už uložený.',
          action: errorData.action || 'scan_new_receipt'
        });
        setShowSuccess(true);
        return;
      }

      if (!storeResponse.ok) {
        let errorMsg = `Server error pri ukladaní (${storeResponse.status})`;
        try {
          const errorData = await storeResponse.json();
          errorMsg = errorData.message || errorMsg;
        } catch (e) {
          // Response wasn't JSON
        }
        throw new Error(errorMsg);
      }

      const storeResult = await storeResponse.json();
      console.log('[processReceiptUID] Receipt stored successfully:', storeResult);
      setStatusMessage('✅ Bloček uložený!');
      await new Promise(resolve => setTimeout(resolve, 300)); // Brief pause to show success

      // Extract receipt_data from response (backend returns {receipt_id, receipt_data, raw_ekasa_data})
      const receiptData = storeResult.receipt_data || storeResult;
      setReceiptData(receiptData);
      setProcessingSuccess(true); // Mark as successful
      setShowSuccess(true);

      // Track successful receipt upload (Slovak QR scan)
      trackEvent('receipt_uploaded', {
        source: 'sk',
        input_method: 'qr_scan',
        merchant: receiptData.merchant || 'Unknown',
        total_amount: receiptData.total_gross || 0,
        currency: receiptData.currency || 'EUR',
        items_count: receiptData.items?.length || 0
      });

      // Reset form if not uploading more bills
      if (moreBills === 'no') {
        console.log('[processReceiptUID] Resetting to scanner view');
        setViewMode('scanner');
        setManualUid('');
        setSelectedFile(null);
      } else {
        console.log('[processReceiptUID] More bills mode - staying on scanner');
      }
    } catch (error: any) {
      console.error('[processReceiptUID] Error caught:', error);

      // Track upload failure
      trackEvent('receipt_upload_failed', {
        input_method: 'qr_scan',
        error_message: error.message || 'Unknown error'
      });

      showError(`Chyba: ${error.message}`);
    } finally {
      setIsProcessing(false);
      setStatusMessage('');
      console.log('[processReceiptUID] Finished');
    }
  };

  // Handle photo upload with client-side QR extraction
  const handlePhotoUploadSubmit = async (file: File) => {
    setIsProcessing(true);
    setStatusMessage('Analyzujem fotku...');

    try {
      // Try client-side QR extraction first
      try {
        const result = await QrScanner.scanImage(file, { returnDetailedScanResult: true });
        const validation = validateSKReceiptQR(result.data);

        if (validation.valid) {
          // Success! Skip upload, process UID directly
          await processReceiptUID(validation.uid);
          return;
        }
      } catch (qrError) {
        console.log('Client-side QR scan failed, falling back to backend...', qrError);
      }

      // Fallback: Upload to backend for OpenAI Vision analysis
      setStatusMessage('Nahrávam fotku na server...');
      const token = await getAuthToken(instance);
      if (!token) {
        throw new Error('Authentication required');
      }

      const fileExtension = file.name.split('.').pop() || '';
      const uploadResponse = await fetch(`${config.apiUrl}/uploadReceipt`, {
        method: 'POST',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          'X-File-Extension': fileExtension,
          'X-Auth-Token': `Bearer ${token}`
        },
        body: file
      });

      if (!uploadResponse.ok) {
        // Parse backend error message for better user feedback
        let errorMessage = 'Nepodarilo sa nahrať súbor';
        try {
          const errorData = await uploadResponse.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // If JSON parsing fails, use generic error
        }
        throw new Error(errorMessage);
      }

      const uploadResult = await uploadResponse.json();

      // Analyze with backend
      setStatusMessage('Analyzujem bloček...');
      const analyzeResponse = await fetch(`${config.apiUrl}/analyzeSKReceiptImage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': `Bearer ${token}`
        },
        body: JSON.stringify({
          uploadId: uploadResult.uploadId,
          blobUrl: uploadResult.blobUrl
        })
      });

      if (analyzeResponse.status === 422) {
        // Parse error response safely instead of rendering HTML directly
        try {
          const errorData = await analyzeResponse.json();
          showError(errorData.message || 'Bloček nemohol byť spracovaný');
        } catch {
          // If JSON parsing fails, show generic error
          showError('Bloček nemohol byť spracovaný. Skontrolujte QR kód.');
        }
        return;
      }

      if (!analyzeResponse.ok) {
        const errorData = await analyzeResponse.json();
        throw new Error(errorData.message || 'Failed to analyze image');
      }

      const analyzeResult = await analyzeResponse.json();

      // Process the extracted UID
      await processReceiptUID(analyzeResult.qrCode);
    } catch (error: any) {
      // Track upload failure (photo upload)
      trackEvent('receipt_upload_failed', {
        input_method: 'photo_upload',
        error_message: error.message || 'Unknown error'
      });

      showError(`Chyba: ${error.message}`);
    } finally {
      setIsProcessing(false);
      setStatusMessage('');
    }
  };

  // OLD FUNCTION - REPLACED BY QR SCANNER FLOWS
  /*
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Validation
    if (!selectedFile) {
      showError('Vyberte súbor s bločkom');
      return;
    }
    if (selectedFile && !selectedFile.type.startsWith('image/')) {
      showError('Podporované sú len obrázkové súbory!');
      return;
    }

    setIsProcessing(true);

    if (selectedFile) {
      try {
        // Step 1: Upload file
        setStatusMessage('Nahrávam tvoju fotografiu...');
        const token = await getAuthToken(instance);
        if (!token) {
          throw new Error('Authentication required');
        }

        const fileExtension = selectedFile.name.split('.').pop() || '';
        const uploadResponse = await fetch(`${config.apiUrl}/uploadReceipt`, {
          method: 'POST',
          headers: {
            'Content-Type': selectedFile.type || 'application/octet-stream',
            'X-File-Extension': fileExtension,
            'X-Auth-Token': `Bearer ${token}`
          },
          body: selectedFile
        });

        if (!uploadResponse.ok) {
          // Parse backend error message for better user feedback
          let errorMessage = 'Nepodarilo sa nahrať súbor';
          try {
            const errorData = await uploadResponse.json();
            errorMessage = errorData.message || errorMessage;
          } catch (parseError) {
            // If JSON parsing fails, use generic error
          }
          throw new Error(errorMessage);
        }

        const uploadResult = await uploadResponse.json();
        
        // Step 2: Analyze the uploaded image
        setStatusMessage('Analyzujem bloček...');
        const analyzeResponse = await authenticatedFetch(`${config.apiUrl}/analyzeSKReceiptImage`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uploadId: uploadResult.uploadId,
            blobUrl: uploadResult.blobUrl
          })
        });
        
        // Check if we got an error response (status 422)
        if (analyzeResponse.status === 422) {
          // Parse error response safely instead of rendering HTML directly
          try {
            const errorData = await analyzeResponse.json();
            showError(errorData.message || 'Bloček nemohol byť spracovaný');
          } catch {
            // If JSON parsing fails, show generic error
            showError('Bloček nemohol byť spracovaný. Skontrolujte QR kód.');
          }
          return;
        }

        // Handle other error statuses
        if (!analyzeResponse.ok) {
          const errorData = await analyzeResponse.json();
          throw new Error(errorData.message || 'Failed to analyze image');
        }

        // Success case - we got JSON data
        const analyzeResult = await analyzeResponse.json();
        // console.log('Analysis result:', analyzeResult);
        
        // Step 3: Validate QR/UID with eKasa
        setStatusMessage('Overujem bloček v systéme eKasa...');
        const validateResponse = await authenticatedFetch(`${config.apiUrl}/getReceiptFromeKasa`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uploadId: uploadResult.uploadId,
            qrCode: analyzeResult.qrCode
          })
        });
        
        // Check if we got an HTML error page (status 422)
        if (validateResponse.status === 422) {
          const htmlContent = await validateResponse.text();
          
          // Replace current page with error page
          document.open();
          document.write(htmlContent);
          document.close();
          return;
        }

        // Handle other error statuses
        if (!validateResponse.ok) {
          const errorData = await validateResponse.json();
          throw new Error(errorData.message || 'Failed to validate receipt');
        }

        // Success case - eKasa validation passed
        const validateResult = await validateResponse.json();
        // console.log('Validation result:', validateResult);
        
        // Step 4: Store receipt data
        setStatusMessage('Ukladám bloček...');
        const storeReceiptResponse = await authenticatedFetch(`${config.apiUrl}/storeReceiptOnly`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uploadId: uploadResult.uploadId,
            validationResult: validateResult,
            action: 'store',
            moreBills: moreBills
          })
        });

        // Check if we got an HTML error page (status 422)
        if (storeReceiptResponse.status === 422) {
          const htmlContent = await storeReceiptResponse.text();

          // Replace current page with error page
          document.open();
          document.write(htmlContent);
          document.close();
          return;
        }

        // Handle other error statuses
        if (!storeReceiptResponse.ok) {
          const errorData = await storeReceiptResponse.json();

          // Handle duplicate receipt error
          if (storeReceiptResponse.status === 409 && errorData.error === 'DUPLICATE_RECEIPT') {
            setStatusMessage('');
            setIsProcessing(false);

            // Show duplicate receipt error page
            setReceiptData({
              isDuplicate: true,
              receipt_hash: errorData.receipt_id,
              message: errorData.user_message || 'Tento bloček je už uložený.',
              action: errorData.action || 'scan_new_receipt'
            });
            setShowSuccess(true); // Reuse the success page but with different content
            return;
          }

          throw new Error(errorData.message || 'Failed to store receipt');
        }

        // Success case - receipt stored (items processing handled automatically by backend)
        const storeReceiptResult = await storeReceiptResponse.json();
        // console.log('Store receipt result:', storeReceiptResult);

        // Set receipt data for success page
        const receiptData = {
          receipt_hash: storeReceiptResult.receipt_id,
          merchant: storeReceiptResult.receipt_data.merchant || 'Neznámy obchodník',
          addr_line: storeReceiptResult.receipt_data.addr_line || '',
          addr_postcode: storeReceiptResult.receipt_data.addr_postcode || '',
          addr_city: storeReceiptResult.receipt_data.addr_city || '',
          addr_country: storeReceiptResult.receipt_data.addr_country || '',
          total_gross: storeReceiptResult.receipt_data.total_gross || 0,
          datetime: storeReceiptResult.receipt_data.datetime || new Date().toISOString(),
          currency: storeReceiptResult.receipt_data.currency || 'EUR'
        };
        setReceiptData(receiptData);

        // Track successful receipt upload (Photo with QR extraction)
        trackEvent('receipt_uploaded', {
          source: 'sk',
          input_method: 'photo_upload',
          merchant: receiptData.merchant,
          total_amount: receiptData.total_gross,
          currency: receiptData.currency
        });

        setStatusMessage('');
        setIsProcessing(false);
        setShowSuccess(true);
        
      } catch (error) {
        // console.error('Error in scan workflow:', error);

        // Track upload failure (photo with QR extraction)
        trackEvent('receipt_upload_failed', {
          input_method: 'photo_upload',
          error_message: String(error) || 'Unknown error',
          workflow: 'scan'
        });

        setStatusMessage('');
        setIsProcessing(false);
        showError(`Chyba pri spracovaní: ${error}`);
      }
    } else if (inputMethod === 'manual' && manualUid) {
      try {
        // Step 1: Validate UID with eKasa
        setStatusMessage('Overujem UID v systéme eKasa...');
        const validateResponse = await authenticatedFetch(`${config.apiUrl}/getReceiptFromeKasa`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uploadId: `manual_${Date.now()}`,
            manualUid: manualUid
          })
        });

        // Check if we got an HTML error page (status 422)
        if (validateResponse.status === 422) {
          const htmlContent = await validateResponse.text();
          
          // Replace current page with error page
          document.open();
          document.write(htmlContent);
          document.close();
          return;
        }

        // Handle other error statuses
        if (!validateResponse.ok) {
          const errorData = await validateResponse.json();
          throw new Error(errorData.message || 'Failed to validate UID');
        }

        // Success case - eKasa validation passed
        const validateResult = await validateResponse.json();
        // console.log('Manual validation result:', validateResult);
        
        // Step 2: Store receipt data
        setStatusMessage('Ukladám bloček...');
        const storeReceiptResponse = await authenticatedFetch(`${config.apiUrl}/storeReceiptOnly`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uploadId: `manual_${Date.now()}`,
            validationResult: validateResult,
            action: 'store',
            moreBills: moreBills
          })
        });

        // Check if we got an HTML error page (status 422)
        if (storeReceiptResponse.status === 422) {
          const htmlContent = await storeReceiptResponse.text();

          // Replace current page with error page
          document.open();
          document.write(htmlContent);
          document.close();
          return;
        }

        // Handle other error statuses
        if (!storeReceiptResponse.ok) {
          const errorData = await storeReceiptResponse.json();

          // Handle duplicate receipt error
          if (storeReceiptResponse.status === 409 && errorData.error === 'DUPLICATE_RECEIPT') {
            setStatusMessage('');
            setIsProcessing(false);

            // Show duplicate receipt error page
            setReceiptData({
              isDuplicate: true,
              receipt_hash: errorData.receipt_id,
              message: errorData.user_message || 'Tento bloček je už uložený.',
              action: errorData.action || 'scan_new_receipt'
            });
            setShowSuccess(true); // Reuse the success page but with different content
            return;
          }

          throw new Error(errorData.message || 'Failed to store receipt');
        }

        // Success case - receipt stored (items processing handled automatically by backend)
        const storeReceiptResult = await storeReceiptResponse.json();
        // console.log('Manual store receipt result:', storeReceiptResult);

        // Set receipt data for success page
        const receiptData = {
          receipt_hash: storeReceiptResult.receipt_id,
          merchant: storeReceiptResult.receipt_data.merchant || 'Neznámy obchodník',
          addr_line: storeReceiptResult.receipt_data.addr_line || '',
          addr_postcode: storeReceiptResult.receipt_data.addr_postcode || '',
          addr_city: storeReceiptResult.receipt_data.addr_city || '',
          addr_country: storeReceiptResult.receipt_data.addr_country || '',
          total_gross: storeReceiptResult.receipt_data.total_gross || 0,
          datetime: storeReceiptResult.receipt_data.datetime || new Date().toISOString(),
          currency: storeReceiptResult.receipt_data.currency || 'EUR'
        };
        setReceiptData(receiptData);

        // Track successful receipt upload (Manual UID entry)
        trackEvent('receipt_uploaded', {
          source: 'sk',
          input_method: 'manual_uid',
          merchant: receiptData.merchant,
          total_amount: receiptData.total_gross,
          currency: receiptData.currency
        });

        setStatusMessage('');
        setIsProcessing(false);
        setShowSuccess(true);
        
      } catch (error) {
        // console.error('Error in manual workflow:', error);

        // Track upload failure (manual UID)
        trackEvent('receipt_upload_failed', {
          input_method: 'manual_uid',
          error_message: String(error) || 'Unknown error',
          workflow: 'manual'
        });

        setStatusMessage('');
        setIsProcessing(false);
        showError(`Chyba pri validácii: ${error}`);
      }
    }
  };
  */

  // Non-SK receipt handlers
  // OLD FUNCTION - REPLACED BY QRSCANNERVIEW BUTTONS
  /*
  const handleNonSkStart = async () => {
    setShowNonSkFlow(true);
    setNonSkStep('country');

    // Fetch countries
    try {
      const response = await authenticatedFetch(`${config.apiUrl}/getCountries`);
      if (response.ok) {
        const result = await response.json();
        setCountries(result.countries || []);
      }
    } catch (error) {
      // console.error('Error fetching countries:', error);
    }
  };
  */



  // Helper to fetch user profile for split prefill
  const handleStartSplit = async () => {
    console.log('🟡 handleStartSplit called, receiptData:', receiptData);

    if (!receiptData?.receipt_id) {
      showError('Chyba: ID bločka nie je dostupné');
      return;
    }

    console.log('🟡 Starting split for receipt:', receiptData.receipt_id);
    setIsSplitProcessing(true);

    try {
      const response = await authenticatedFetch(`${config.apiUrl}/startSplit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          receipt_id: receiptData.receipt_id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to start split');
      }

      const result = await response.json();
      console.log('🟡 Got split token:', result.token);
      setSplitToken(result.token);
      setShowSplitFlow(true);

      // Track split started
      trackEvent('split_started', {
        receipt_id: receiptData.receipt_id
      });

    } catch (error) {
      // console.error('Error starting split:', error);
      showError(`Chyba pri spustení rozdelenia: ${error}`);
    } finally {
      setIsSplitProcessing(false);
    }
  };

  // Show Bill Split Flow
  if (showSplitFlow) {
    return (
      <BillSplitFlow
        receiptData={receiptData}
        instance={instance}
        splitToken={splitToken}
        onComplete={() => setShowSplitFlow(false)}
        onCancel={() => setShowSplitFlow(false)}
        splitFromReceiptDetail={splitFromReceiptDetail}
        splitReceiptId={splitReceiptId}
      />
    );
  }

  // Show Non-SK Receipt Flow
  if (showNonSkFlow) {
    return (
      <NonSkReceiptFlow
        onSuccess={(receiptData) => {
          setReceiptData(receiptData);
          setShowSuccess(true);
          setShowNonSkFlow(false);
        }}
        onCancel={() => {
          setShowNonSkFlow(false);
          setViewMode('scanner');
        }}
        authenticatedFetch={authenticatedFetch}
        showError={showError}
      />
    );
  }

  // Show note confirmation page after note is saved
  if (showNoteConfirmation && receiptData && savedNote) {
    return (
      <div style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: '#667eea',
        padding: '20px',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '30px',
          margin: '0 auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxWidth: '400px',
          width: '100%',
          animation: 'slideUp 0.6s ease-out'
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: '#10b981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 30px',
              animation: 'bounce 0.8s ease-out 0.3s both',
              position: 'relative'
            }}>
              <div style={{
                width: '20px',
                height: '35px',
                border: 'solid white',
                borderWidth: '0 4px 4px 0',
                transform: 'rotate(45deg)',
                marginTop: '-8px'
              }} />
            </div>
            <h1 style={{ fontSize: '24px', color: '#333', marginBottom: '10px' }}>
              Poznámka uložená
            </h1>
            <p style={{ color: '#666', fontSize: '16px' }}>
              Úspešne pridaná k dokladu
            </p>
          </div>

          {/* Note Info */}
          <div style={{
            background: '#f0fdf4',
            borderRadius: '12px',
            padding: '20px',
            margin: '25px 0',
            borderLeft: '4px solid #10b981'
          }}>
            <div style={{ marginBottom: '15px', fontSize: '14px' }}>
              <span style={{
                color: '#065f46',
                fontWeight: 600,
                display: 'block',
                marginBottom: '5px'
              }}>
                📄 ID dokladu:
              </span>
              <div style={{
                color: '#047857',
                background: 'white',
                padding: '8px 12px',
                borderRadius: '6px',
                wordBreak: 'break-word',
                border: '1px solid #d1fae5'
              }}>
                {receiptData.receipt_hash}
              </div>
            </div>
            <div style={{ fontSize: '14px' }}>
              <span style={{
                color: '#065f46',
                fontWeight: 600,
                display: 'block',
                marginBottom: '5px'
              }}>
                📝 Uložená poznámka:
              </span>
              <div style={{
                color: '#047857',
                background: 'white',
                padding: '8px 12px',
                borderRadius: '6px',
                wordBreak: 'break-word',
                border: '1px solid #d1fae5'
              }}>
                {savedNote}
              </div>
            </div>
          </div>

          {/* View Detail Button */}
          <button
            onClick={() => {
              // Use receipt_id if available (for non-SK receipts), otherwise construct from receipt_hash
              const receiptId = receiptData.receipt_id
                || (receiptData.receipt_hash.startsWith('R_')
                  ? receiptData.receipt_hash
                  : `R_${receiptData.receipt_hash}`);
              navigate(`/receipts/${receiptId}`);
            }}
            style={{
              width: '100%',
              padding: '15px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: 600,
              cursor: 'pointer',
              marginTop: '10px',
              marginBottom: '100px',
              textDecoration: 'none',
              display: 'inline-block',
              textAlign: 'center',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#5a67d8';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#667eea';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            🔍 Otvoriť detail bločku
          </button>
        </div>

        <style>
          {`
            @keyframes slideUp {
              from {
                opacity: 0;
                transform: translateY(30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes bounce {
              0% { transform: scale(0); }
              50% { transform: scale(1.1); }
              100% { transform: scale(1); }
            }
          `}
        </style>
      </div>
    );
  }

  // Show success page if processing completed successfully
  if (showSuccess && receiptData) {
    // Show duplicate receipt error page
    if (receiptData.isDuplicate) {
      return (
        <div style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          background: '#667eea',
          padding: '20px',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            margin: '0 auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            maxWidth: '400px',
            width: '100%',
            animation: 'slideUp 0.6s ease-out'
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: '#f59e0b',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 30px',
                animation: 'bounce 0.8s ease-out 0.3s both',
                position: 'relative'
              }}>
                <div style={{
                  fontSize: '36px',
                  color: 'white'
                }}>⚠️</div>
              </div>
              <h1 style={{ fontSize: '24px', color: '#333', marginBottom: '10px' }}>
                Bloček už existuje
              </h1>
              <p style={{ color: '#666', fontSize: '16px' }}>
                {receiptData.message}
              </p>
            </div>

            {/* Receipt Info */}
            <div style={{
              background: '#fef3cd',
              borderRadius: '12px',
              padding: '20px',
              margin: '25px 0',
              borderLeft: '4px solid #f59e0b'
            }}>
              <div style={{ fontSize: '14px' }}>
                <span style={{
                  color: '#92400e',
                  fontWeight: 600,
                  display: 'block',
                  marginBottom: '5px'
                }}>
                  📄 ID existujúceho dokladu:
                </span>
                <div style={{
                  color: '#b45309',
                  background: 'white',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  wordBreak: 'break-all',
                  border: '1px solid #fbbf24'
                }}>
                  {receiptData.receipt_hash}
                </div>
              </div>
            </div>

            {/* New Receipt Button */}
            <button
              onClick={handleNewReceipt}
              style={{
                width: '100%',
                padding: '15px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                marginTop: '10px',
                textDecoration: 'none',
                display: 'inline-block',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#5a67d8';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#667eea';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              📄 Nahrať iný bloček
            </button>
          </div>

          <style>
            {`
              @keyframes slideUp {
                from {
                  opacity: 0;
                  transform: translateY(30px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
              @keyframes bounce {
                0% { transform: scale(0); }
                50% { transform: scale(1.1); }
                100% { transform: scale(1); }
              }
            `}
          </style>
        </div>
      );
    }

    // Show normal success page
    return (
      <div style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: '#667eea',
        padding: '20px',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '30px',
          margin: '0 auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxWidth: '400px',
          width: '100%',
          animation: 'slideUp 0.6s ease-out'
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: '#10b981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 30px',
              animation: 'bounce 0.8s ease-out 0.3s both',
              position: 'relative'
            }}>
              <div style={{
                width: '20px',
                height: '35px',
                border: 'solid white',
                borderWidth: '0 4px 4px 0',
                transform: 'rotate(45deg)',
                marginTop: '-8px'
              }} />
            </div>
            <h1 style={{ fontSize: '24px', color: '#333', marginBottom: '10px' }}>
              Bloček uložený
            </h1>
            <p style={{ color: '#666', fontSize: '16px' }}>
              Úspešne spracovaný a uložený do databázy
            </p>
          </div>

          {/* Receipt Info */}
          <div style={{
            background: '#f9fafb',
            borderRadius: '12px',
            padding: '20px',
            margin: '25px 0',
            borderLeft: '4px solid #10b981'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', fontSize: '14px' }}>
              <span style={{ color: '#6b7280', fontWeight: 500 }}>ID bločka:</span>
              <span style={{ color: '#1f2937', fontWeight: 600, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>
                {receiptData.receipt_hash}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', fontSize: '14px' }}>
              <span style={{ color: '#6b7280', fontWeight: 500 }}>Obchodník:</span>
              <span style={{ color: '#1f2937', fontWeight: 600, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>
                {receiptData.merchant}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', fontSize: '14px' }}>
              <span style={{ color: '#6b7280', fontWeight: 500 }}>Adresa:</span>
              <span style={{ color: '#1f2937', fontWeight: 600, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>
                {receiptData.addr_line}, {receiptData.addr_postcode} {receiptData.addr_city}, {receiptData.addr_country}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', fontSize: '14px' }}>
              <span style={{ color: '#6b7280', fontWeight: 500 }}>Suma:</span>
              <span style={{ color: '#10b981', fontWeight: 700, fontSize: '18px', textAlign: 'right' }}>
                {receiptData.total_gross} EUR
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' }}>
              <span style={{ color: '#6b7280', fontWeight: 500 }}>Dátum:</span>
              <span style={{ color: '#1f2937', fontWeight: 600, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>
                {formatDateTime(receiptData.datetime)}
              </span>
            </div>
          </div>

          {/* Note Section */}
          <div style={{
            background: '#fff7ed',
            borderRadius: '12px',
            padding: '20px',
            margin: '25px 0',
            borderLeft: '4px solid #fb923c'
          }}>
            <label style={{
              color: '#9a3412',
              fontWeight: 600,
              fontSize: '16px',
              marginBottom: '10px',
              display: 'block'
            }}>
              📝 Poznámka k dokladu
            </label>
            <textarea
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #fed7aa',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                minHeight: '80px',
                transition: 'border-color 0.3s ease',
                background: 'white',
                boxSizing: 'border-box'
              }}
              placeholder="Pridaj poznámku k tomuto dokladu (napríklad účel nákupu, komu patrí, atď.)"
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onFocus={(e) => e.target.style.borderColor = '#fb923c'}
              onBlur={(e) => e.target.style.borderColor = '#fed7aa'}
            />
            <button
              style={{
                width: '100%',
                padding: '12px',
                background: isSavingNote ? '#fed7aa' : '#fb923c',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: isSavingNote ? 'not-allowed' : 'pointer',
                marginTop: '10px',
                transition: 'all 0.3s ease'
              }}
              onClick={handleSaveNote}
              disabled={isSavingNote}
              onMouseEnter={(e) => {
                if (!isSavingNote) {
                  e.currentTarget.style.background = '#ea580c';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSavingNote) {
                  e.currentTarget.style.background = '#fb923c';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              {isSavingNote ? '⏳ Ukladám...' : '💾 Uložiť poznámku'}
            </button>
            {noteStatus.message && (
              <div style={{
                textAlign: 'center',
                marginTop: '10px',
                fontSize: '14px',
                fontWeight: 500,
                color: noteStatus.type === 'success' ? '#10b981' : '#ef4444'
              }}>
                {noteStatus.message}
              </div>
            )}
          </div>

          {/* View Detail Button - Secondary Action */}
          <button
            onClick={() => {
              // Use receipt_id if available (for non-SK receipts), otherwise construct from receipt_hash
              const receiptId = receiptData.receipt_id
                || (receiptData.receipt_hash.startsWith('R_')
                  ? receiptData.receipt_hash
                  : `R_${receiptData.receipt_hash}`);
              navigate(`/receipts/${receiptId}`);
            }}
            style={{
              width: '100%',
              padding: '15px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
              marginTop: '20px',
              textDecoration: 'none',
              display: 'inline-block',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              boxSizing: 'border-box',
              border: '2px solid #667eea',
              background: 'white',
              color: '#667eea'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f5f7ff';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            📋 Otvoriť detail bločku
          </button>

          {/* Split Bill Explanation */}
          <div style={{
            textAlign: 'center',
            color: '#666',
            fontSize: '16px',
            marginTop: '20px',
            marginBottom: '15px',
            padding: '15px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            lineHeight: '1.5'
          }}>
            Ak je tento bloček spoločný napríklad za obed, večeru, alebo nákup. Môžete ho jednoducho rozdeliť. Nastavíte komu a čo prideliť, a my vám spočítame jednotlivé podiely.
          </div>

          {/* Split Bill Button */}
          <button
            onClick={handleStartSplit}
            disabled={isSplitProcessing}
            style={{
              width: '100%',
              padding: '15px',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: 600,
              cursor: isSplitProcessing ? 'not-allowed' : 'pointer',
              marginBottom: '15px',
              textDecoration: 'none',
              display: 'inline-block',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              boxSizing: 'border-box',
              border: 'none',
              background: isSplitProcessing ? '#cbd5e1' : '#10b981',
              color: 'white'
            }}
            onMouseEnter={(e) => {
              if (!isSplitProcessing) {
                e.currentTarget.style.background = '#059669';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSplitProcessing) {
                e.currentTarget.style.background = '#10b981';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            {isSplitProcessing ? '⏳ Pripravujem...' : '🧮 Rozdeliť bloček'}
          </button>

          {/* Add Another Receipt Button - Primary Action at Bottom */}
          <button
            onClick={handleNewReceipt}
            style={{
              width: '100%',
              padding: '15px',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: '20px',
              textDecoration: 'none',
              display: 'inline-block',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              boxSizing: 'border-box',
              border: 'none',
              background: '#667eea',
              color: 'white'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#5a67d8';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#667eea';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            📄 Pridať ďalší bloček
          </button>
        </div>

        <style>
          {`
            @keyframes slideUp {
              from {
                opacity: 0;
                transform: translateY(30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes bounce {
              0% { transform: scale(0); }
              50% { transform: scale(1.1); }
              100% { transform: scale(1); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <>
      <div style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: '18px',
      lineHeight: '1.5',
      background: '#667eea',
      color: '#111827',
      margin: 0,
      minHeight: '100vh',
      padding: 'max(10px, env(safe-area-inset-top)) clamp(10px, 4vw, 20px) max(12px, calc(env(safe-area-inset-bottom) + 8px)) clamp(10px, 4vw, 20px)',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale'
    }}>
      <div style={{
        width: 'min(720px, 100%)',
        margin: '0 auto',
        background: '#fff',
        borderRadius: '14px',
        padding: 'clamp(16px, 4vw, 28px)',
        boxShadow: '0 4px 12px rgba(0,0,0,.08)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '22px' }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: '#667eea',
            display: 'grid',
            placeItems: 'center',
            margin: '0 auto 14px'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style={{ width: '30px', height: '30px', stroke: '#fff', strokeWidth: 2, fill: 'none' }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10,9 9,9 8,9"/>
            </svg>
          </div>
          <h1 style={{ fontSize: 'clamp(22px, 6vw, 30px)', margin: '0 0 6px', color: '#111827' }}>
            Nahrať bloček
          </h1>
          <p style={{ color: '#6b7280', fontSize: 'clamp(15px, 4vw, 17px)', lineHeight: '1.5' }}>
            {viewMode === 'scanner' && 'Namier kameru na QR kód bločka alebo si vyber iný spôsob pridania nižšie.'}
            {viewMode === 'photo-upload' && 'Nahrajte fotografiu QR kódu z bločka (napr. screenshot z aplikácie obchodu).'}
            {viewMode === 'manual-uid' && 'Ak je QR kód zle vytlačený, alebo sa nedá nascanovať, môžeš ID bločka (UID) prepísať aj ručne.'}
            {viewMode === 'non-sk' && 'Nahrajte bloček z inej krajiny ako Slovensko.'}
          </p>
        </div>

        {/* QR Scanner View (default) */}
        {viewMode === 'scanner' && !showSuccess && !showNoteConfirmation && !showSplitFlow && !showNonSkFlow && (
          <QRScannerView
            onScanSuccess={handleQRScanSuccess}
            onError={showError}
            onPhotoUpload={handlePhotoUploadClick}
            onManualInput={handleManualInputClick}
            onNonSkReceipt={handleNonSkClick}
            moreBills={moreBills}
            onMoreBillsChange={setMoreBills}
            statusMessage={statusMessage}
            isProcessing={isProcessing}
            processingSuccess={processingSuccess}
          />
        )}

        {/* Photo Upload & Manual UID Flow */}
        {(viewMode === 'photo-upload' || viewMode === 'manual-uid') && !showSuccess && !showNoteConfirmation && !showSplitFlow && !showNonSkFlow && (
          <PhotoUploadFlow
            instance={instance}
            onReceiptProcessed={handleReceiptProcessed}
            onBackToScanner={() => setViewMode('scanner')}
            initialMode={viewMode === 'manual-uid' ? 'manual-uid' : 'photo-upload'}
          />
        )}

        {/* OLD CODE - TO BE REMOVED */}
        {false && viewMode === 'photo-upload-OLD' && !showSuccess && !showNoteConfirmation && !showSplitFlow && !showNonSkFlow && (
          <div>
            {/* Back button */}
            <button
              onClick={() => setViewMode('scanner')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#667eea',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: '16px',
                padding: '8px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              ← Späť na skener
            </button>

            <form onSubmit={(e) => {
              e.preventDefault();
              if (selectedFile) {
                handlePhotoUploadSubmit(selectedFile);
              } else {
                showError('Vyberte súbor');
              }
            }}>
              {/* File Upload */}
              <div style={{ margin: '20px 0' }}>
                <div
                  style={{
                    border: '2px dashed #e5e7eb',
                    borderRadius: '12px',
                    padding: 'clamp(18px, 5vw, 28px)',
                    textAlign: 'center',
                    background: '#f9fafb',
                    cursor: 'pointer',
                    transition: 'border-color .2s, background .2s',
                    borderColor: selectedFile ? '#10b981' : '#e5e7eb',
                    backgroundColor: selectedFile ? '#f0fdf4' : '#f9fafb'
                  }}
                  onClick={() => document.getElementById('fileInput')?.click()}
                >
                  <input
                    type="file"
                    id="fileInput"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    aria-label="Nahrať obrázok bločku"
                  />
                  {!selectedFile && (
                    <svg style={{ width: '44px', height: '44px', margin: '0 auto 8px', stroke: '#9ca3af' }}
                         xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round"
                            d="M7 16a 4 4 0 0 1-.88-7.903A5 5 0 1 1 15.9 6L16 6a5 5 0 0 1 1 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                    </svg>
                  )}
                  <div style={{
                    color: selectedFile ? '#065f46' : '#4b5563',
                    fontSize: 'clamp(16px, 4.5vw, 18px)',
                    fontWeight: selectedFile ? 700 : 'normal'
                  }}>
                    {selectedFile ? `✅ ${selectedFile.name}` : (
                      <>
                        Kliknite pre výber súboru
                        <small style={{ display: 'block', color: '#9ca3af', fontSize: '14px', marginTop: '4px' }}>
                          Fotogaléria • Fotoaparát • Súbory
                        </small>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Status messages for photo upload */}
              {isProcessing && (
                <div style={{
                  background: '#eef2ff',
                  border: '1px solid #667eea',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#667eea', fontSize: '16px', fontWeight: 600 }}>
                    {statusMessage}
                  </div>
                </div>
              )}

              {errorMessage && (
                <div style={{
                  background: '#fef2f2',
                  border: '2px solid #ef4444',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px',
                  color: '#dc2626',
                  fontSize: '14px'
                }}>
                  {errorMessage}
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={isProcessing || !selectedFile}
                style={{
                  width: '100%',
                  minHeight: '52px',
                  padding: '14px 16px',
                  background: (isProcessing || !selectedFile) ? '#cbd5e1' : '#667eea',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '14px',
                  fontSize: '19px',
                  fontWeight: 700,
                  cursor: (isProcessing || !selectedFile) ? 'not-allowed' : 'pointer',
                  marginTop: '16px'
                }}
              >
                {isProcessing ? 'Spracovávam...' : '📄 Spracovať bloček'}
              </button>
            </form>
          </div>
        )}

        {/* Manual UID Input View - OLD CODE */}
        {false && viewMode === 'manual-uid-OLD' && !showSuccess && !showNoteConfirmation && !showSplitFlow && !showNonSkFlow && (
          <div>
            {/* Back button */}
            <button
              onClick={() => setViewMode('scanner')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#667eea',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: '16px',
                padding: '8px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              ← Späť na skener
            </button>

            <form onSubmit={(e) => {
              e.preventDefault();
              if (manualUid.trim()) {
                const validation = validateSKReceiptQR(manualUid.trim());
                if (validation.valid) {
                  processReceiptUID(validation.uid);
                } else {
                  showError('Neplatný formát UID. Skontrolujte zadaný kód.');
                }
              } else {
                showError('Zadajte UID bločku');
              }
            }}>
              {/* Manual UID input */}
              <div style={{ margin: '20px 0' }}>
                <div style={{
                  border: '2px solid #667eea',
                  borderRadius: '12px',
                  padding: 'clamp(16px, 4.5vw, 22px)',
                  background: '#eef2ff'
                }}>
                  <svg style={{ width: '40px', height: '40px', stroke: '#667eea', margin: '0 auto 10px', display: 'block' }}
                       xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                    Zadajte UID z vytlačeného bločka
                  </div>
                  <input
                    type="text"
                    value={manualUid}
                    onChange={(e) => setManualUid(e.target.value)}
                    placeholder="O-89E5AA494F2245B8..."
                    maxLength={50}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '15px',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      background: '#fff',
                      boxSizing: 'border-box'
                    }}
                  />
                  <div style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    background: '#f9fafb',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    marginTop: '8px',
                    wordBreak: 'break-all',
                    overflowWrap: 'break-word'
                  }}>
                    <div style={{ marginBottom: '4px' }}>Formát: O-XXXXXXX...</div>
                    <div>Príklad: O-89E5AA494F2245B89E5AA494F2255B8E5</div>
                  </div>
                </div>
              </div>

              {/* Status messages for manual UID */}
              {isProcessing && (
                <div style={{
                  background: '#eef2ff',
                  border: '1px solid #667eea',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#667eea', fontSize: '16px', fontWeight: 600 }}>
                    {statusMessage}
                  </div>
                </div>
              )}

              {errorMessage && (
                <div style={{
                  background: '#fef2f2',
                  border: '2px solid #ef4444',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px',
                  color: '#dc2626',
                  fontSize: '14px'
                }}>
                  {errorMessage}
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={isProcessing || !manualUid.trim()}
                style={{
                  width: '100%',
                  minHeight: '52px',
                  padding: '14px 16px',
                  background: (isProcessing || !manualUid.trim()) ? '#cbd5e1' : '#667eea',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '14px',
                  fontSize: '19px',
                  fontWeight: 700,
                  cursor: (isProcessing || !manualUid.trim()) ? 'not-allowed' : 'pointer'
                }}
              >
                {isProcessing ? 'Spracovávam...' : '🔤 Spracovať UID'}
              </button>
            </form>
          </div>
        )}

          {/* More Bills Toggle - OLD CODE (now handled in PhotoUploadFlow) */}
          {false && viewMode !== 'scanner' && !showSuccess && !showNoteConfirmation && !showSplitFlow && !showNonSkFlow && (
            <div style={{ margin: '20px 0' }}>
              <label style={{
                display: 'block',
                fontWeight: 600,
                marginBottom: '8px',
                color: '#111827',
                fontSize: 'clamp(16px, 4.5vw, 18px)'
              }}>
                Budeš nahrávať ďalší bloček?
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '12px 14px'
              }}>
                <span style={{
                  fontSize: 'clamp(14px, 4vw, 16px)',
                  color: moreBills === 'no' ? '#667eea' : '#9ca3af',
                  fontWeight: moreBills === 'no' ? 700 : 500,
                  transition: 'color .2s, font-weight .2s',
                  whiteSpace: 'nowrap'
                }}>
                  Nie
                </span>
                <div
                  style={{
                    position: 'relative',
                    width: '72px',
                    height: '40px',
                    flex: '0 0 72px',
                    background: moreBills === 'yes' ? '#667eea' : '#e5e7eb',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    transition: 'background .25s ease',
                    outline: 'none'
                  }}
                  onClick={() => setMoreBills(moreBills === 'no' ? 'yes' : 'no')}
                >
                  <div style={{
                    content: '""',
                    position: 'absolute',
                    top: '3px',
                    left: moreBills === 'yes' ? '35px' : '3px',
                    width: '34px',
                    height: '34px',
                    background: '#fff',
                    borderRadius: '50%',
                    boxShadow: '0 2px 4px rgba(0,0,0,.2)',
                    transition: 'left .25s ease'
                  }} />
                </div>
                <span style={{
                  fontSize: 'clamp(14px, 4vw, 16px)',
                  color: moreBills === 'yes' ? '#667eea' : '#9ca3af',
                  fontWeight: moreBills === 'yes' ? 700 : 500,
                  transition: 'color .2s, font-weight .2s',
                  whiteSpace: 'nowrap'
                }}>
                  Áno
                </span>
              </div>
            </div>
          )}

        {/* Non-SK Receipt Button - keep this outside for backward compat */}
        {!showSuccess && !showNoteConfirmation && !showSplitFlow && !showNonSkFlow && viewMode === 'scanner' && (
          <button
            onClick={handleNonSkClick}
            style={{
              width: '100%',
              minHeight: '52px',
              display: 'none', // Hidden since it's now in QRScanner View
              alignItems: 'center',
              justifyContent: 'center',
              padding: '14px 16px',
            gap: '8px',
            background: '#f3f4f6',
            color: '#374151',
            border: '2px solid #d1d5db',
            borderRadius: '14px',
            fontSize: '19px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'transform .1s ease, background .2s ease',
            marginBottom: '12px'
          }}
        >
          🌍 Mám neslovenský bloček
        </button>
        )}

      </div>
      
      <style>
        {`
          @keyframes progress {
            0% { transform: translateX(-100%); }
            50% { transform: translateX(0%); }
            100% { transform: translateX(100%); }
          }
        `}
      </style>
    </div>

    </>
  );
}

export default AddReceiptPage;